/**
 * Unified Event Consumer Hook
 *
 * Central dispatcher that receives all WebSocket messages via the existing
 * useWebSocket `addMessageListener` pattern and routes them to the
 * appropriate Zustand stores based on event channel.
 *
 * Architecture:
 *   useWebSocket (connection) → addMessageListener → useUnifiedEventConsumer (routing)
 *     → useNotificationStore (notifications channel)
 *     → useEventStore (debugging / connection state)
 *     → TanStack Query invalidation (assets channel — handled by useDerivedAssetEvents)
 *
 * NOTE: Execution events are handled by useExecutionWebSocket (shared connection consumer).
 * Asset derived events are still handled by useDerivedAssetEvents.
 * This consumer handles notification + system channels only (for now).
 */

import { useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQueryClient, QueryKey } from "@tanstack/react-query";
import { useEventStore } from "@/stores/useEventStore";
import { useNotificationStore } from "@/stores/useNotificationStore";
import {
  isUnifiedEvent,
  getEventChannel,
  EVENT_TYPES,
} from "@/types/events.types";
import type {
  UnifiedEvent,
  NotificationCreatedData,
  NotificationBatchReadData,
} from "@/types/events.types";
import { CREDIT_BALANCE_QUERY_KEY } from "@/hooks/useCreditBalance";
import { BILLABLE_ACCOUNTS_QUERY_KEY } from "@/hooks/useBillableAccounts";
import { PAYMENTS_QUERY_KEY } from "@/hooks/useTeamBilling";
import {
  AVAILABLE_PLANS_QUERY_KEY,
  MY_SUBSCRIPTION_QUERY_KEY,
} from "@/hooks/useAvailablePlans";
import { logger } from "@/services/logger";
import { generateUUID } from "@/utils/uuid";
import { useEntitlementDebugStore } from "@/stores/useEntitlementDebugStore";
import { useCollaboration } from "@/contexts/CollaborationContext";
import { useWorkflowCanvasStore } from "@/stores/useWorkflowCanvasStore";
import { workflowLockSessionKey } from "@/utils/lockSessionStorage";

/**
 * Subset of the lock-event payload emitted by api-alpha
 * `src/services/lockBroadcast.ts`. Only the fields the consumer reads
 * are included; the actor's userId comes via `metadata.userId` per the
 * publisher convention, not via `eventData`.
 */
interface LockEventData {
  workflowId?: string;
  timelineId?: string;
  projectId?: string;
  lockedBy?: string | null;
  lockedByName?: string | null;
  lockedAt?: string | null;
  lockExpiresAt?: string | null;
  refreshIntervalSeconds?: number | null;
}

type AddMessageListener = (listener: (message: unknown) => void) => () => void;

/**
 * Credit-balance activity events. Per the A-lite refactor these are per-op:
 * `credit-reserved` / `credit-settled` / `credit-released` (wire eventType
 * `activity.credit-<op>`), each signalling a balance change. The balance is NOT
 * on the wire — signal-only — so the consumer invalidates the balance query and
 * lets useCreditBalance refetch. A `credit-` prefix match covers all three plus
 * the multi-grant `credit-grant-burned` sub-event (Path A #2) and any future
 * credit-* event; matches either the fully-qualified wire eventType or the
 * ledger slug in `eventTypeSlug`.
 *
 * Multi-grant burn: one settle can emit N `credit-grant-burned` children + a
 * `credit-settled` summary. The DB parents them all under the request event
 * (parent_event_id, power#824); on the client we get the same effect cheaply by
 * routing the balance invalidation through the shared debounce, so a burst of
 * credit events from one request collapses into a single refetch instead of one
 * per event. (Today api-alpha only pushes the summary over WS; the debounce also
 * future-proofs for when the children are published.)
 */
function isCreditBalanceActivityEvent(
  eventType: string,
  data: unknown,
): boolean {
  const slug = (data as { eventTypeSlug?: string } | null | undefined)
    ?.eventTypeSlug;
  return (
    eventType.startsWith("activity.credit-") ||
    (slug?.startsWith("credit-") ?? false)
  );
}

/**
 * Subscription lifecycle events. Like credit events they ride the activity
 * channel and are signal-only — the balance/plan is NOT on the wire — so the
 * consumer invalidates the affected queries and lets them refetch. Matches the
 * fully-qualified wire eventType or the ledger slug in `eventTypeSlug`.
 */
function isSubscriptionActivityEvent(
  eventType: string,
  data: unknown,
): boolean {
  const slug = (data as { eventTypeSlug?: string } | null | undefined)
    ?.eventTypeSlug;
  return (
    eventType.startsWith("activity.subscription-") ||
    (slug?.startsWith("subscription-") ?? false)
  );
}

/** True for the payment-failure sub-event specifically. */
function isSubscriptionPaymentFailedEvent(
  eventType: string,
  data: unknown,
): boolean {
  const slug = (data as { eventTypeSlug?: string } | null | undefined)
    ?.eventTypeSlug;
  return (
    eventType === "activity.subscription-payment-failed" ||
    slug === "subscription-payment-failed"
  );
}

/**
 * Debounce window for batching query invalidations.
 * Multiple asset events within this window will be batched into
 * a single invalidation pass, reducing API calls during video chunking.
 */
const INVALIDATION_DEBOUNCE_MS = 500;

/**
 * Hooks into the existing WebSocket connection and routes unified v2.0
 * events to the appropriate stores.
 *
 * @param addMessageListener - The listener registration function from useWebSocket
 */
export function useUnifiedEventConsumer(
  addMessageListener: AddMessageListener | undefined,
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const recordEvent = useEventStore((s) => s.recordEvent);
  const { setLock } = useCollaboration();

  // Pending invalidation keys, batched and deduplicated
  const pendingInvalidations = useRef<Set<string>>(new Set());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flush all pending invalidations
  const flushInvalidations = useCallback(() => {
    if (pendingInvalidations.current.size === 0) return;

    const keys = Array.from(pendingInvalidations.current);
    pendingInvalidations.current.clear();

    keys.forEach((serializedKey) => {
      const queryKey: QueryKey = JSON.parse(serializedKey);
      queryClient.invalidateQueries({ queryKey });
    });
  }, [queryClient]);

  // Schedule an invalidation (debounced)
  const scheduleInvalidation = useCallback(
    (queryKey: QueryKey) => {
      const serialized = JSON.stringify(queryKey);
      pendingInvalidations.current.add(serialized);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(
        flushInvalidations,
        INVALIDATION_DEBOUNCE_MS,
      );
    },
    [flushInvalidations],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        // Flush any pending invalidations before unmount
        flushInvalidations();
      }
    };
  }, [flushInvalidations]);

  // Notification store actions
  const handleNotificationCreated = useNotificationStore(
    (s) => s.handleNotificationCreated,
  );
  const handleNotificationRead = useNotificationStore(
    (s) => s.handleNotificationRead,
  );
  const handleNotificationBatchRead = useNotificationStore(
    (s) => s.handleNotificationBatchRead,
  );
  const handleNotificationDeleted = useNotificationStore(
    (s) => s.handleNotificationDeleted,
  );
  const incrementUnreadCount = useNotificationStore(
    (s) => s.incrementUnreadCount,
  );

  const routeEvent = useCallback(
    (event: UnifiedEvent) => {
      const { channel, eventType, data } = event;

      // Record all events for debugging
      recordEvent(event);

      // Workflow / timeline lock events. Handled explicitly because they
      // share the "execution" channel default but are managed separately
      // from execution events.
      //
      // Owner-tab guard: if this tab issued the ACQUIRE / DELETE itself,
      // its own API response handler has already updated state (and
      // sessionStorage holds the lockId for refresh). Re-applying the
      // event here would clobber the lockId and force a needless refetch
      // round-trip. Other tabs / users have no sessionStorage entry, so
      // they fall through and update normally.
      if (eventType === EVENT_TYPES.WORKFLOW_LOCK_ACQUIRED) {
        const lockData = data as LockEventData;
        if (lockData.workflowId) {
          const currentWorkflowId =
            useWorkflowCanvasStore.getState().workflowId;
          if (lockData.workflowId !== currentWorkflowId) return;
          if (
            sessionStorage.getItem(workflowLockSessionKey(lockData.workflowId))
          )
            return;
          setLock({
            workflowId: lockData.workflowId,
            isLocked: true,
            isOwner: false,
            lockedBy: lockData.lockedBy ?? undefined,
            lockedByName: lockData.lockedByName ?? undefined,
            lockedAt: lockData.lockedAt ?? undefined,
            lockExpiresAt: lockData.lockExpiresAt ?? undefined,
          });
        }
        return;
      }

      if (eventType === EVENT_TYPES.WORKFLOW_LOCK_RELEASED) {
        const lockData = data as LockEventData;
        if (lockData.workflowId) {
          const currentWorkflowId =
            useWorkflowCanvasStore.getState().workflowId;
          if (lockData.workflowId !== currentWorkflowId) return;
          if (
            sessionStorage.getItem(workflowLockSessionKey(lockData.workflowId))
          )
            return;
          setLock(null);
        }
        return;
      }

      // Timeline lock events are consumed by useTimelineLock directly via
      // useWebSocketContext.addMessageListener — that hook owns per-timeline
      // state, so the bridge lives there. We let timeline events fall through
      // to the channel switch below; the "execution" channel case is a no-op
      // for them (lock event types do not match any handled eventType).

      switch (channel) {
        case "notifications":
          if (eventType === EVENT_TYPES.NOTIFICATION_CREATED) {
            handleNotificationCreated(data as NotificationCreatedData);
            incrementUnreadCount();
          } else if (eventType === EVENT_TYPES.NOTIFICATION_READ) {
            const readData = data as { notificationId: string };
            handleNotificationRead(readData.notificationId);
          } else if (eventType === EVENT_TYPES.NOTIFICATION_BATCH_READ) {
            handleNotificationBatchRead(data as NotificationBatchReadData);
          } else if (eventType === EVENT_TYPES.NOTIFICATION_DELETED) {
            const deleteData = data as { notificationId: string };
            handleNotificationDeleted(deleteData.notificationId);
          }
          break;

        case "system":
          if (eventType === EVENT_TYPES.EMAIL_SETTINGS_UPDATED) {
            queryClient.invalidateQueries({ queryKey: ["contact-emails"] });
            queryClient.invalidateQueries({ queryKey: ["email-preferences"] });
          } else {
            // Other system events (announcements, maintenance) — will be handled
            // by a system store in Stage 5 or later. For now, just log.
            logger.info("System event received", {
              eventType,
              correlationId: event.metadata.correlationId,
            });
          }
          break;

        // v2.0 asset events — invalidate TanStack Query caches for instant UI updates
        // Uses debouncing to batch multiple events during video chunking (see issue #293)
        case "assets": {
          const assetData = data as {
            assetId?: string;
            projectId?: string;
          };
          if (assetData.assetId) {
            scheduleInvalidation(["derived-assets", assetData.assetId]);
          }
          if (assetData.projectId) {
            scheduleInvalidation(["assets", assetData.projectId]);
            // Also schedule project refresh for thumbnail updates
            scheduleInvalidation(["project", assetData.projectId]);
          }
          break;
        }

        // Entitlement counter/policy changes — invalidate all entitlement-bearing queries
        case "entitlements": {
          logger.info("Entitlement event received, invalidating queries", {
            eventType,
            correlationId: event.metadata.correlationId,
          });
          if (import.meta.env.DEV) {
            useEntitlementDebugStore
              .getState()
              .log(
                "ws_event",
                "useUnifiedEventConsumer",
                `WS entitlement event: ${eventType}. Invalidating projects, workflows, timelines, entitlement-policies queries.`,
                undefined,
                event,
              );
          }
          queryClient.invalidateQueries({ queryKey: ["projects"] });
          queryClient.invalidateQueries({ queryKey: ["workflow"] });
          queryClient.invalidateQueries({ queryKey: ["workflows"] });
          queryClient.invalidateQueries({ queryKey: ["timelines"] });
          queryClient.invalidateQueries({
            queryKey: ["entitlement-policies"],
          });
          break;
        }

        // Activity channel. The per-op credit events ride here
        // (activity.credit-reserved / -settled / -released / -grant-burned).
        // The balance is NOT on the wire — signal-only, so we invalidate the
        // balance query and let useCreditBalance refetch (the 60s backstop
        // covers any missed event). Routed through the debounce so a multi-grant
        // burn's burst of events collapses into ONE refetch per request.
        // Other activity events are handled by useActivityEvents.
        case "activity":
          if (isCreditBalanceActivityEvent(eventType, data)) {
            scheduleInvalidation(CREDIT_BALANCE_QUERY_KEY);
            // The header account switcher shows a balance per account; the WS
            // payload has no subscription_id, so blanket-invalidate the whole
            // accounts list to refresh every visible row (debounced with the
            // balance invalidation, so a multi-grant burst collapses into one
            // refetch each).
            scheduleInvalidation(BILLABLE_ACCOUNTS_QUERY_KEY);
            // A top-up mint rides here (credit-balance-changed) — refresh the
            // payment history so the new top-up charge appears. Blanket-
            // invalidate by prefix (the WS payload has no subscription_id).
            scheduleInvalidation(PAYMENTS_QUERY_KEY);
          } else if (isSubscriptionActivityEvent(eventType, data)) {
            // Subscription lifecycle. On a payment failure, surface a toast with
            // remediation and refresh the subscription so the UI reflects the
            // failed state; the subscription card / return page derive from it.
            if (isSubscriptionPaymentFailedEvent(eventType, data)) {
              queryClient.invalidateQueries({
                queryKey: MY_SUBSCRIPTION_QUERY_KEY,
              });
              toast.error(t("settings.billing.toast.paymentFailed"));
            } else {
              // A plan change touches the plan, subscription, and both credit
              // surfaces (allowance may change). Debounced so a burst collapses.
              scheduleInvalidation(MY_SUBSCRIPTION_QUERY_KEY);
              scheduleInvalidation(AVAILABLE_PLANS_QUERY_KEY);
              scheduleInvalidation(CREDIT_BALANCE_QUERY_KEY);
              scheduleInvalidation(BILLABLE_ACCOUNTS_QUERY_KEY);
              // A subscription-changed event may carry a new subscription
              // payment — refresh the payment history too (prefix-invalidated).
              scheduleInvalidation(PAYMENTS_QUERY_KEY);
            }
          }
          break;

        // execution events are handled by useExecutionWebSocket
        case "execution":
          break;
      }
    },
    [
      queryClient,
      t,
      recordEvent,
      setLock,
      scheduleInvalidation,
      handleNotificationCreated,
      handleNotificationRead,
      handleNotificationBatchRead,
      handleNotificationDeleted,
      incrementUnreadCount,
    ],
  );

  useEffect(() => {
    if (!addMessageListener) return;

    const correlationId = generateUUID();
    logger.info("Unified event consumer registered", { correlationId });

    const removeListener = addMessageListener((message: unknown) => {
      // Only handle v2.0 unified events
      // Legacy v1.0 events are handled by existing hooks (execution, derived assets)
      if (isUnifiedEvent(message)) {
        routeEvent(message);
        return;
      }

      // v1.0 events: upgrade notification + entitlement events to v2.0 for processing.
      // Other v1.0 events are handled by dedicated hooks (execution, derived assets).
      if (
        typeof message === "object" &&
        message !== null &&
        "eventType" in message
      ) {
        const legacy = message as {
          eventType: string;
          eventData?: unknown;
          timestamp?: string;
          metadata?: Record<string, unknown>;
        };

        // On (re)connect, invalidate entitlement queries to catch missed updates
        if (legacy.eventType === "connection.established") {
          if (import.meta.env.DEV) {
            useEntitlementDebugStore
              .getState()
              .log(
                "ws_reconnect",
                "useUnifiedEventConsumer",
                "WebSocket reconnected. Invalidating projects, workflows, timelines, entitlement-policies queries.",
              );
          }
          queryClient.invalidateQueries({ queryKey: ["projects"] });
          queryClient.invalidateQueries({ queryKey: ["workflow"] });
          queryClient.invalidateQueries({ queryKey: ["workflows"] });
          queryClient.invalidateQueries({ queryKey: ["timelines"] });
          queryClient.invalidateQueries({
            queryKey: ["entitlement-policies"],
          });
          return;
        }

        const channel = getEventChannel(legacy.eventType);

        // Upgrade and process notification + entitlement events from v1.0.
        // Activity events are otherwise handled by useActivityEvents, but the
        // credit-balance activity event is routed here too so case "activity"
        // can invalidate the balance query (signal-only refetch).
        // Other v1.0 events are handled by existing hooks.
        if (
          channel === "notifications" ||
          channel === "entitlements" ||
          (channel === "activity" &&
            (isCreditBalanceActivityEvent(legacy.eventType, legacy.eventData) ||
              isSubscriptionActivityEvent(legacy.eventType, legacy.eventData)))
        ) {
          const upgraded: UnifiedEvent = {
            envelopeId: generateUUID(),
            eventType: legacy.eventType,
            channel,
            data: legacy.eventData ?? {},
            metadata: {
              timestamp: legacy.timestamp ?? new Date().toISOString(),
              correlationId: (legacy.metadata?.correlationId as string) ?? "",
              eventId: (legacy.metadata?.correlationId as string) ?? "",
              userId: (legacy.metadata?.userId as string) ?? "",
            },
            version: "2.0",
          };
          routeEvent(upgraded);
        }
      }
    });

    return () => {
      logger.info("Unified event consumer unregistered", { correlationId });
      removeListener();
    };
  }, [addMessageListener, routeEvent]);
}
